<template>
    <div class="modal fade show hotel-modal" aria-labelledby="checkoutModalTitle">
        <div class="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
            <div class="modal-content">
                <div class="d-flex justify-content-between align-items-center gap-2 px-2 my-1">
                    <h1 class="modal-title-text mb-0">{{ modalTitle }}</h1>
                    <div class="d-flex align-items-center gap-1 flex-shrink-0">
                        <button v-if="!isHousekeeping" type="button" class="btn btn-sm btn-info" @click="$emit('onSeeDetails', reservation)">
                            Details Bill
                        </button>
                        <button @click="closeModal" type="button" class="btn-close" aria-label="Close"></button>
                    </div>
                </div>

                <hr class="mt-0">

                <Loader v-if="loading"/>

                <div v-if="!loading" class="modal-body">
                    <div class="row g-2">
                    <div class="col-lg-7">
                    <div class="detail-section mb-2">
                        <div class="section-title">Reservation Details</div>
                        <div class="row g-1">
                            <div class="col-md-3 col-6">
                                <div class="detail-label">Reservation No</div>
                                <div class="detail-value">{{ textValue(reservation.reservation_no) }}</div>
                            </div>
                            <div class="col-md-3 col-6">
                                <div class="detail-label">Status</div>
                                <div class="detail-value">
                                    <span class="badge" :class="statusClass">{{ statusLabel }}</span>
                                </div>
                            </div>
                            <div class="col-md-3 col-6">
                                <div class="detail-label">Guest</div>
                                <div class="detail-value">{{ textValue(reservation.guest_name) }}</div>
                            </div>
                            <div class="col-md-3 col-6">
                                <div class="detail-label">Mobile No</div>
                                <div class="detail-value">{{ textValue(reservation.guest_mobile) }}</div>
                            </div>
                            <div class="col-md-3 col-6">
                                <div class="detail-label">Check-in</div>
                                <div class="detail-value">{{ textValue(reservation.check_in_formatted) }}</div>
                            </div>
                            <div class="col-md-3 col-6">
                                <div class="detail-label">Check-out</div>
                                <div class="detail-value">{{ textValue(reservation.check_out_formatted) }}</div>
                            </div>
                            <div class="col-md-3 col-6">
                                <div class="detail-label">Nights</div>
                                <div class="detail-value">{{ textValue(reservation.nights) }}</div>
                            </div>
                            <div class="col-md-3 col-6">
                                <div class="detail-label">Payment</div>
                                <div class="detail-value">
                                    <span class="badge" :class="paymentClass">{{ paymentLabel }}</span>
                                </div>
                            </div>
                            <div v-if="reservation.booked_by" class="col-md-3 col-6">
                                <div class="detail-label">Booked By</div>
                                <div class="detail-value">{{ reservation.booked_by.name }}</div>
                            </div>
                            <div v-if="reservation.referrer" class="col-md-3 col-6">
                                <div class="detail-label">Referrer</div>
                                <div class="detail-value">{{ reservation.referrer.name }}</div>
                            </div>
                            <div v-if="reservation.notes" class="col-12">
                                <div class="detail-label">Notes</div>
                                <div class="detail-value notes-value">{{ reservation.notes }}</div>
                            </div>
                        </div>
                    </div>

                    <div v-if="roomRows.length" class="detail-section mb-2">
                        <div class="section-title">Rooms</div>
                        <div class="table-responsive">
                            <table class="table table-sm table-bordered detail-table mb-0">
                                <thead>
                                    <tr>
                                        <th>Room</th>
                                        <th class="text-end">Rate</th>
                                        <th class="text-end">Nights</th>
                                        <th class="text-end">Amount</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="room in roomRows" :key="room.service_resource_id">
                                        <td>{{ room.name }}</td>
                                        <td class="text-end">{{ commaFormat(room.rate) }}</td>
                                        <td class="text-end">{{ nightCount }}</td>
                                        <td class="text-end">{{ commaFormat(room.amount) }}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>

                    </div>

                    <div class="col-lg-5">
                    <div class="row g-1 mb-2">
                        <div class="col-4">
                            <div class="summary-card summary-total">
                                <span class="summary-label">Estimate Bill</span>
                                <span class="summary-value">{{ commaFormat(reservation.total) }}</span>
                            </div>
                        </div>
                        <div class="col-4">
                            <div class="summary-card summary-paid">
                                <span class="summary-label">Paid</span>
                                <span class="summary-value">{{ commaFormat(reservation.paid) }}</span>
                            </div>
                        </div>
                        <div class="col-4">
                            <div class="summary-card summary-due">
                                <span class="summary-label">Due</span>
                                <span class="summary-value">{{ commaFormat(reservation.due) }}</span>
                            </div>
                        </div>
                    </div>

                    <div v-if="paymentRows.length" class="detail-section mb-2">
                        <div class="section-title">Payment History</div>
                        <div class="table-responsive payment-scroll">
                            <table class="table table-sm table-bordered detail-table mb-0">
                                <thead>
                                    <tr>
                                        <th>Date</th>
                                        <th>Voucher</th>
                                        <th>Received In</th>
                                        <th>Reference</th>
                                        <th class="text-end">Amount</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="payment in paymentRows" :key="payment.id">
                                        <td>{{ textValue(payment.receipt_date) }}</td>
                                        <td>{{ textValue(payment.voucher_no) }}</td>
                                        <td>{{ textValue(payment.account_head_name) }}</td>
                                        <td>{{ textValue(payment.receipt_reference) }}</td>
                                        <td class="text-end">{{ commaFormat(payment.total_paid_amount) }}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>

                    <div class="detail-section mb-2">
                        <div class="section-title">Check-out Date</div>
                        <input type="date" v-model="checkoutDate" class="form-control form-control-sm">
                    </div>
                    </div>

                    <div class="col-12">
                    <div v-if="hasChecklist" class="checklist mb-2">
                        <div class="checklist-title">Checkout Checklist</div>
                        <div class="checklist-grid">
                            <div
                                v-for="item in checklist"
                                :key="item.note_master_id"
                                class="checklist-item"
                                :class="{ 'is-checked': item.checked }"
                            >
                                <label class="checklist-row">
                                    <input type="checkbox" v-model="item.checked" class="checklist-input">
                                    <span class="tick" :class="{ 'tick-on': item.checked }">
                                        <i class="fa fa-check"></i>
                                    </span>
                                    <span class="checklist-label">{{ item.label }}</span>
                                </label>
                                <textarea
                                    v-if="item.checked"
                                    v-model="item.description"
                                    class="form-control checklist-desc"
                                    rows="1"
                                    placeholder="Add a note (optional)"
                                ></textarea>
                            </div>
                        </div>
                    </div>
                    </div>
                    </div>

                    <div v-if="!isHousekeeping && isSettled" class="alert-note">
                        Confirm check out for this guest. The rooms will be released.
                    </div>
                    <div v-if="!isHousekeeping && !isSettled" class="alert-note alert-note-danger">
                        This reservation is not settled yet. Settle the bill from Detail Billing before checking out.
                    </div>
                    <div v-if="isHousekeeping" class="alert-note">
                        Current status: {{ currentStatusLabel }}. Update the checklist and save a new status.
                    </div>

                    <div class="d-flex flex-wrap justify-content-end gap-1 mt-2 modal-actions">
                        <button type="button" class="btn btn-outline-secondary" @click="closeModal">Cancel</button>
                        <button
                            v-if="isHousekeeping && !isEdit"
                            type="button"
                            class="btn btn-warning"
                            :disabled="saving"
                            @click="onConfirm('on_claim')"
                        >
                            On Claim
                        </button>
                        <button
                            v-if="isHousekeeping && !isEdit"
                            type="button"
                            class="btn btn-success"
                            :disabled="saving"
                            @click="onConfirm('ready_for_checkout')"
                        >
                            Ready for Checkout
                        </button>
                        <button
                            v-if="isEdit"
                            type="button"
                            class="btn btn-primary"
                            :disabled="saving"
                            @click="onConfirm(editStatus)"
                        >
                            Update
                        </button>
                        <button
                            v-if="!isHousekeeping && isSettled"
                            type="button"
                            class="btn btn-primary"
                            :disabled="!checkoutDate || saving"
                            @click="onConfirm('released')"
                        >
                            Confirm Check Out
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { computed, inject, onMounted, ref } from 'vue';
import { useRoute } from 'vue-router';
import Loader from '@/components/atom/LoaderComponent';
import handleHospital from '@/services/modules/hospital';
import handleHotel from '@/services/modules/hotel/hotel';
import useDate from '@/services/utils/day';
import figureFormatter from '@/services/utils/figureFormatter';

const props = defineProps({
    reservationId: {
        type: Number,
        required: true,
    },
    isHousekeeping: {
        type: Boolean,
        default: false,
    },
    editStatus: {
        type: String,
        default: '',
    },
});

const emit = defineEmits(['onClose', 'onCheckedOut', 'onSeeDetails']);

const $route = useRoute();
const showError = inject('showError');
const showSuccess = inject('showSuccess');
const { notesByType } = handleHospital();
const { fetchSingleHotelReservation, checkoutHotelReservation } = handleHotel();
const { currentDate } = useDate();
const { commaFormat } = figureFormatter();

const loading = ref(false);
const saving = ref(false);
const reservation = ref({});
const checklist = ref([]);
const checkoutDate = ref(currentDate());

const hasChecklist = computed(() => checklist.value.length > 0);

const isEdit = computed(() => !!props.editStatus);

const isSettled = computed(() => reservation.value.settlement_status === 'settled');

const modalTitle = computed(() => {
    if (isEdit.value) return 'Update Housekeeping Status';
    if (props.isHousekeeping) return 'Housekeeping Status';
    return 'Check Out';
});

const statusLabels = {
    ready_for_checkout: 'Ready for Checkout',
    on_claim: 'On Claim',
    checked_in: 'Checked In',
};

const currentStatusLabel = computed(() => {
    const status = reservation.value.registration_status;
    if (!status) return '—';
    if (!statusLabels[status]) return status;
    return statusLabels[status];
});

const statusBadges = {
    pending: 'bg-light-warning',
    confirmed: 'bg-light-info',
    checked_in: 'bg-light-success',
    ready_for_checkout: 'bg-light-success',
    on_claim: 'bg-light-warning',
    released: 'bg-light-secondary',
    completed: 'bg-light-secondary',
    cancelled: 'bg-light-danger',
};

const paymentBadges = {
    paid: 'bg-light-success',
    partial: 'bg-light-warning',
    unpaid: 'bg-light-danger',
};

const humanize = (value) => {
    if (!value) return '-';
    return value.split('_').map((word) => {
        return word.charAt(0).toUpperCase() + word.slice(1);
    }).join(' ');
};

const textValue = (value) => {
    if (value === null || value === undefined || value === '') return '-';
    return value;
};

const statusLabel = computed(() => humanize(reservation.value.registration_status));

const paymentLabel = computed(() => humanize(reservation.value.payment_status));

const statusClass = computed(() => {
    const badge = statusBadges[reservation.value.registration_status];
    if (!badge) return 'bg-light-secondary';
    return badge;
});

const paymentClass = computed(() => {
    const badge = paymentBadges[reservation.value.payment_status];
    if (!badge) return 'bg-light-secondary';
    return badge;
});

const nightCount = computed(() => {
    if (!reservation.value.nights) return 1;
    return Number(reservation.value.nights);
});

const roomRows = computed(() => {
    if (!reservation.value.rooms) return [];
    return reservation.value.rooms.map((room) => {
        return Object.assign({}, room, { amount: Number(room.rate) * nightCount.value });
    });
});

const paymentRows = computed(() => {
    if (!reservation.value.payments) return [];
    return reservation.value.payments;
});

const getReservation = () => {
    loading.value = true;
    const query = `?company_id=${$route.params.companyId}`;
    return fetchSingleHotelReservation(query, props.reservationId).then(res => {
        if (!res.status) {
            showError(res.message);
            return;
        }
        reservation.value = res.data;
        if (!res.data.check_out) return;
        checkoutDate.value = res.data.check_out;
    }).catch(() => {
        showError('Something went wrong');
    }).finally(() => {
        loading.value = false;
    });
};

const savedChecklistMap = () => {
    const saved = reservation.value.checkout_checklist;
    if (!saved) return {};
    return saved.reduce((map, item) => {
        map[item.note_master_id] = item;
        return map;
    }, {});
};

const buildChecklistItem = (item, saved) => {
    const savedItem = saved[item.id];
    if (!savedItem) {
        return {
            note_master_id: item.id,
            label: item.name,
            checked: false,
            description: '',
        };
    }
    return {
        note_master_id: item.id,
        label: item.name,
        checked: true,
        description: savedItem.description,
    };
};

const getChecklist = () => {
    const query = `?company_id=${$route.params.companyId}`;
    return notesByType(query, 'checkout_checklist').then(res => {
        if (!res.status) return;
        const saved = savedChecklistMap();
        checklist.value = res.data.map(item => buildChecklistItem(item, saved));
    });
};

const buildCheckState = () => {
    return checklist.value
        .filter(item => item.checked)
        .map(item => ({
            note_master_id: item.note_master_id,
            label: item.label,
            checked: item.checked,
            description: item.description,
        }));
};

const closeModal = () => {
    emit('onClose');
};

const completionStatusFor = (status) => {
    if (status === 'released') return 'completed';
    return status;
};

const onConfirm = (status = 'released') => {
    if (!checkoutDate.value) return;
    if (!props.reservationId) return;
    if (saving.value) return;
    if (status === 'released' && !isSettled.value) return;
    const payload = {
        company_id: $route.params.companyId,
        reg_status: status,
        completion_status: completionStatusFor(status),
        check_out: checkoutDate.value,
        checkout_checklist: buildCheckState(),
    };
    saving.value = true;
    checkoutHotelReservation(payload, props.reservationId).then(res => {
        if (!res.status) {
            showError(res.message);
            return;
        }
        showSuccess(res.message);
        emit('onCheckedOut');
    }).catch(() => {
        showError('Something went wrong');
    }).finally(() => {
        saving.value = false;
    });
};

onMounted(async () => {
    await getReservation();
    await getChecklist();
});
</script>

<style scoped>
.hotel-modal {
    display: block;
    background: #1c1d1c33;
    overflow-y: auto;
}
.modal-title-text {
    font-size: 1.25rem;
    line-height: 1.3;
    word-break: break-word;
    min-width: 0;
}
@media (max-width: 575.98px) {
    .modal-title-text {
        font-size: 1.05rem;
    }
    .modal-body {
        padding: 0.5rem;
    }
    .modal-dialog {
        margin: 0.4rem;
    }
}
.section-title {
    font-size: 0.7rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    color: #5e5873;
    margin-bottom: 0.3rem;
}
.detail-section {
    background: #f8f8f8;
    border: 1px solid #ebe9f1;
    border-radius: 0.4rem;
    padding: 0.45rem 0.55rem;
}
.detail-label {
    display: block;
    font-size: 0.63rem;
    line-height: 1.3;
    text-transform: uppercase;
    letter-spacing: 0.02em;
    color: #6e6b7b;
    opacity: 0.85;
}
.detail-value {
    font-size: 0.8rem;
    font-weight: 600;
    line-height: 1.3;
    color: #384250;
    word-break: break-word;
}
.notes-value {
    max-height: 42px;
    overflow-y: auto;
    font-weight: 500;
}
@media (max-width: 991.98px) {
    .notes-value {
        max-height: 70px;
    }
}
.detail-table {
    min-width: 0;
    font-size: 0.76rem;
}
.detail-table th,
.detail-table td {
    padding: 0.25rem 0.4rem;
    white-space: nowrap;
}
.summary-card {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    justify-content: center;
    text-align: right;
    padding: 0.4rem 0.6rem;
    border-radius: 0.4rem;
    border: 1px solid transparent;
    height: 100%;
}
.summary-label {
    font-size: 0.6rem;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.02em;
    line-height: 1.2;
    white-space: nowrap;
    opacity: 0.75;
    margin-bottom: 0.1rem;
}
.summary-value {
    font-size: 0.9rem;
    font-weight: 700;
    line-height: 1.2;
}
.summary-total {
    background: #eef2f7;
    border-color: #dfe6ef;
    color: #384250;
}
.summary-paid {
    background: #e7f6ec;
    border-color: #cdeed8;
    color: #1a7f43;
}
.summary-due {
    background: #fdeceb;
    border-color: #f8d5d2;
    color: #c0392b;
}
@media (max-width: 767.98px) {
    .detail-section {
        padding: 0.5rem;
    }
    .summary-value {
        font-size: 0.85rem;
    }
    .summary-label {
        white-space: normal;
    }
    .summary-card {
        padding: 0.4rem;
    }
}
.modal-actions .btn {
    font-size: 0.85rem;
}
@media (max-width: 575.98px) {
    .modal-actions .btn {
        flex: 1 1 100%;
    }
}
.alert-note {
    padding: 0.4rem 0.6rem;
    border-radius: 0.4rem;
    background: #fff8e6;
    border: 1px solid #f5e2b3;
    color: #8a6d1a;
    font-size: 0.78rem;
}
.payment-scroll {
    max-height: 190px;
    overflow-x: auto;
    overflow-y: auto;
}
.alert-note-danger {
    background: #fdeaea;
    border-color: #f5c2c2;
    color: #a3251c;
}
.checklist {
    border: 1px solid #eceef1;
    border-radius: 0.4rem;
    padding: 0.4rem 0.5rem;
}
.checklist-title {
    font-size: 0.7rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    color: #5e5873;
    margin-bottom: 0.3rem;
}
.checklist-grid {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 0.35rem;
    align-items: stretch;
}
@media (max-width: 991.98px) {
    .checklist-grid {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }
}
@media (max-width: 575.98px) {
    .checklist-grid {
        grid-template-columns: minmax(0, 1fr);
    }
}
.checklist-item {
    display: flex;
    flex-direction: column;
    padding: 0.3rem 0.45rem;
    border: 1px solid #eceef1;
    border-radius: 0.5rem;
    background: #fbfcfd;
    transition: border-color 0.15s ease, background 0.15s ease;
}
.checklist-item.is-checked {
    border-color: #b7e0c4;
    background: #f2fbf5;
}
.checklist-row {
    display: flex;
    align-items: flex-start;
    gap: 0.45rem;
    cursor: pointer;
    margin-bottom: 0;
    flex-grow: 1;
}
.checklist-desc {
    margin-top: 0.3rem;
    width: 100%;
    padding: 0.2rem 0.4rem;
    font-size: 0.75rem;
    resize: vertical;
}
.modal-body {
    padding: 0.5rem 0.75rem 0.75rem;
}
.checklist-input {
    display: none;
}
.tick {
    flex-shrink: 0;
    margin-top: 1px;
    width: 17px;
    height: 17px;
    border-radius: 5px;
    border: 1.5px solid #d0d5dd;
    display: flex;
    align-items: center;
    justify-content: center;
    color: transparent;
    font-size: 0.7rem;
    transition: all 0.15s ease;
}
.tick-on {
    background: #1fa363;
    border-color: #1fa363;
    color: #ffffff;
}
.checklist-label {
    font-size: 0.78rem;
    color: #475467;
    line-height: 1.3;
    word-break: break-word;
}
.checklist-item.is-checked .checklist-label {
    color: #101828;
    font-weight: 600;
}
</style>
